<template>
  <div class="image-button" role="button" tabindex="0" @click="onClick" @keydown="onKeyDown">
    <div
      class="label"
      :class="{ 'has-start': hasLabelStart() }"
      v-if="hasLabelRow() && labelPosition === 'top'"
    >
      <slot name="label-start" />
      <TipTap v-if="fields.label?.key" v-model="value" mode="single" />
    </div>

    <div class="image-wrapper" :class="{ 'full-bleed': fields.imageFullBleed }">
      <PlainImage
        :image="fields.image"
        :skeleton-color="skeletonColor"
        :shimmer-color="shimmerColor"
        class="image-frame"
      />
      <div class="label overlay" v-if="fields.label?.key && labelPosition === 'overlay'">
        <TipTap v-model="value" mode="single" />
      </div>
    </div>

    <div
      class="label"
      :class="{ 'has-start': hasLabelStart() }"
      v-if="hasLabelRow() && labelPosition === 'bottom'"
    >
      <slot name="label-start" />
      <TipTap v-if="fields.label?.key" v-model="value" mode="single" />
    </div>
  </div>
</template>

<script setup lang="ts">
import TipTap from '@render/lib/TipTap'
import PlainImage from '../../generic/PlainImage.vue'
import { computed, useSlots } from 'vue'
import { useTranslation } from '@render/composables/useTranslation'
import { type ImageFieldData } from '@render/composables/useImagePreset'
import {
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@/render/composables/useStyleData'
import type { TranslationKeyField } from '@/api'
import type { ColorField, SizeField } from '../../../types/styles'
import type { ImageButtonFields } from './definition'

const fields = defineModel<ImageButtonFields>('fields', {
  required: true,
  default: () => ({
    image: {
      type: 'image_field',
      id: '',
    } satisfies ImageFieldData,
    label: { type: 'translation_key', key: '' } satisfies TranslationKeyField,
    labelPosition: 'bottom',
  }),
})

const value = useTranslation(() => fields.value.label?.key)

// `label-start` sits before the caption in the top/bottom label row (SelectionList
// puts its checkbox there), so the row renders even when the caption is empty.
// Functions, not computeds: `slots` is not reactive, so a computed would keep the
// answer from the first render.
const slots = useSlots()
const hasLabelStart = () => !!slots['label-start']
const hasLabelRow = () => !!fields.value.label?.key || hasLabelStart()

const skeletonColor = computed(
  () => getColorStyleData(fields.value.imageSkeletonBgColor) ?? 'transparent',
)
const shimmerColor = computed(
  () => getColorStyleData(fields.value.imageSkeletonShimmerColor) ?? 'transparent',
)

function color(f: ColorField | undefined, fallback: string) {
  return getColorStyleData(f) || fallback
}
function size(f: SizeField | undefined, fallback: string) {
  return getSizeStyleData(f) || fallback
}

const labelPosition = computed(() => fields.value.labelPosition || 'bottom')

// Image margin: single value applied to top/left/right; bottom is always 0 so the
// label sits flush to the image's bottom edge (textMargin-top handles the gap).
const imageMarginCss = computed(() => {
  if (fields.value.imageFullBleed) return '0'
  const v = getSizeStyleData(fields.value.imageSpacing, 'var(--space-12)') ?? 'var(--space-12)'
  return `${v} ${v} 0 ${v}`
})

const textMarginCss = computed(() =>
  fields.value.textMargin ? getSpacingStyleData(fields.value.textMargin) : 'var(--space-12)',
)

// When the image sits flush against the label (imageSpacing is 0), flatten
// the corners that actually meet the label so there's no visible gap between
// the rounded image and the rectangular label.
const isZeroImageSpacing = computed(() => {
  const f = fields.value.imageSpacing
  // Unset → the component default (var(--space-12)) applies, which is non-zero.
  if (!f) return false
  if (f.type === 'size_raw') {
    const raw = f.data.data
    return raw === '' || raw === '0' || Number(raw) === 0
  }
  return false
})

const imageRadiusCss = computed(() => {
  // Full bleed: the image follows the frame's inner curve instead of its own radius.
  const r = fields.value.imageFullBleed
    ? `calc(${size(fields.value.borderRadius, 'var(--radius-md)')} - ${size(fields.value.borderWidth, 'var(--border-width)')})`
    : size(fields.value.imageBorderRadius, 'var(--radius-sm)')
  if (!hasLabelRow() || !(fields.value.imageFullBleed || isZeroImageSpacing.value)) return r
  const pos = fields.value.labelPosition || 'bottom'
  if (pos === 'bottom') return `${r} ${r} 0 0` // tl tr br bl → flatten bottom
  if (pos === 'top') return `0 0 ${r} ${r}` // flatten top
  return r // overlay: label is inside, keep radius
})

const css = computed(() => ({
  imageWidth: size(fields.value.imageWidth, '100%'),
  imageHeight: size(fields.value.imageHeight, '200px'),
  imageObjectFit: fields.value.imageObjectFit || 'cover',
  imageMargin: imageMarginCss.value,
  imageBg: color(fields.value.imageBgColor, 'var(--color-background-subtle-accent)'),
  imageRadius: imageRadiusCss.value,
  imageHoverTransform: fields.value.disableImageZoom ? 'none' : 'scale(1.05)',
  bg: color(fields.value.bgColor, 'var(--color-action-tertiary-background)'),
  bgActive: color(fields.value.bgColorActive, 'var(--color-action-tertiary-background-pressed)'),
  labelColor: color(fields.value.labelColor, 'var(--color-action-tertiary-text)'),
  labelFontSize: size(fields.value.labelFontSize, 'var(--text-label-md-size)'),
  labelLineHeight: size(fields.value.labelLineHeight, 'var(--text-label-md-line-height)'),
  labelFontWeight: fields.value.labelFontWeight || 'var(--text-label-md-weight)',
  borderColor: color(fields.value.borderColor, 'var(--color-action-tertiary-border)'),
  borderColorHover: color(
    fields.value.borderColorHover,
    'var(--color-action-tertiary-border-pressed)',
  ),
  borderRadius: size(fields.value.borderRadius, 'var(--radius-md)'),
  borderWidth: size(fields.value.borderWidth, 'var(--border-width)'),
  textMargin: textMarginCss.value,
}))

const emit = defineEmits<{
  (e: 'onClick'): void
}>()

function onClick() {
  emit('onClick')
}

// Only treat space/enter as "activate" when the button root itself has focus.
// If focus is on a descendant (the TipTap label editor), the keystroke
// belongs to that editor — swallowing it here would break typing spaces
// inside the label.
function onKeyDown(event: KeyboardEvent) {
  if (event.target !== event.currentTarget) return
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  emit('onClick')
}
</script>

<style scoped lang="scss">
.image-button {
  cursor: pointer;
  background-color: v-bind('css.bg');
  width: 100%;
  margin: 0 auto;

  display: flex;
  flex-direction: column;

  border-width: v-bind('css.borderWidth');
  border-style: solid;
  border-color: v-bind('css.borderColor');
  border-radius: v-bind('css.borderRadius');

  transition:
    box-shadow 0.2s ease,
    border-color 0.2s ease;

  > .label {
    margin: v-bind('css.textMargin');
    text-align: center;

    color: v-bind('css.labelColor');
    font-family: var(--text-label-md-family);
    font-size: v-bind('css.labelFontSize');
    line-height: v-bind('css.labelLineHeight');
    font-weight: v-bind('css.labelFontWeight');

    &.has-start {
      display: flex;
      align-items: center;
      gap: var(--space-12);
      text-align: start;
    }

    &.overlay {
      margin: 0;
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      padding: var(--space-12);
      color: var(--color-text-inverse);
      background: linear-gradient(to top, rgba(0, 0, 0, 0.75), transparent);
      z-index: 2;
    }
  }

  > .image-wrapper {
    margin: v-bind('css.imageMargin');
    border-radius: v-bind('css.imageRadius');
    background-color: v-bind('css.imageBg');
    display: flex;
    align-items: center;
    justify-content: center;

    position: relative;

    overflow: visible;

    clip-path: polygon(0 -100%, 100% -100%, 100% 100%, 0 100%);

    > .image-frame {
      width: v-bind('css.imageWidth');
      height: v-bind('css.imageHeight');
      object-fit: v-bind('css.imageObjectFit');

      border-radius: v-bind('css.imageRadius');
      overflow: hidden;

      position: relative;
      z-index: 1;

      transform-origin: bottom center;
      transition: transform 0.2s ease-out;
    }

    > .label.overlay {
      z-index: 2;
    }

    // Edge to edge, so a zoom must stay inside the card rather than rise above it.
    &.full-bleed {
      overflow: hidden;
      clip-path: none;

      > .image-frame {
        transform-origin: center;
      }
    }
  }

  // `:focus-visible` (keyboard) stays unconditional; gate `:hover` behind a hover-capable
  // pointer so touch devices don't keep a tapped card highlighted after a tap-driven page
  // navigation (touch emulates `:hover` on tap and it sticks until the next interaction).
  &:focus-visible {
    outline: none;
    border-color: v-bind('css.borderColorHover');
    box-shadow: var(--shadow-surface-raised);

    > .image-wrapper > .image-frame {
      transform: v-bind('css.imageHoverTransform');
      z-index: 10;
    }
  }

  @media (hover: hover) {
    &:hover {
      outline: none;
      border-color: v-bind('css.borderColorHover');
      box-shadow: var(--shadow-surface-raised);

      > .image-wrapper > .image-frame {
        transform: v-bind('css.imageHoverTransform');
        z-index: 10;
      }
    }
  }

  &:active {
    background-color: v-bind('css.bgActive');
    transform: translateY(1px);
  }
}
</style>
